import { ReactElement, useCallback } from 'react';
import { Trans } from 'react-i18next';

import { Translate, useBoolean } from '@dreryk/react-common';
import {
   Link,
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalWidth,
   RemoveIcon,
   StrongText,
   StyledFlex,
} from '@dreryk/react-components';

import { boundServiceTranslationKeys } from '../../translations';

import { HealthcareProviderPatientPortalBoundServiceRemoveButton } from './HealthcareProviderPatientPortalBoundServiceRemoveButton';

type HealthcareProviderPatientPortalBoundServiceRemoveProps = {
   readonly boundServiceId: number;
   readonly patientPortalServiceName: string;
   readonly receptionServiceName: string;
   readonly onBoundServiceRemoveSuccess: VoidFunction;
};

type ServiceNameProps = {
   readonly serviceName: string;
};

const ServiceName = (props: ServiceNameProps): ReactElement => {
   return <StrongText testId={'serviceName'}>{props.serviceName}</StrongText>;
};

export const HealthcareProviderPatientPortalBoundServiceRemove = (
   props: HealthcareProviderPatientPortalBoundServiceRemoveProps
): ReactElement => {
   const [{ setTrue: openBoundServiceRemoveModal, setFalse: closeBoundServiceRemoveModal }, modalOpened] = useBoolean();

   const handleRemoveSuccess = useCallback(() => {
      closeBoundServiceRemoveModal();
      props.onBoundServiceRemoveSuccess();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [closeBoundServiceRemoveModal, props.onBoundServiceRemoveSuccess]);

   return (
      <>
         <Link testId={'boundServiceRemove'} icon={<RemoveIcon />} onClick={openBoundServiceRemoveModal} />
         {modalOpened ? (
            <Modal
               testId={'boundServiceRemove'}
               title={boundServiceTranslationKeys.removingBoundService}
               width={ModalWidth.NORMAL}
            >
               <StyledFlex direction={'column'} gap={'24px'}>
                  <Translate>{boundServiceTranslationKeys.boundServiceRemoveConfirmation}</Translate>
                  <StyledFlex direction={'column'} gap={'12px'}>
                     <div>
                        <Trans
                           i18nKey={boundServiceTranslationKeys.patientPortalServiceWithName}
                           components={{
                              patientPortalServiceName: <ServiceName serviceName={props.patientPortalServiceName} />,
                           }}
                        />
                     </div>
                     <div>
                        <Trans
                           i18nKey={boundServiceTranslationKeys.receptionServiceWithName}
                           components={{
                              receptionServiceName: <ServiceName serviceName={props.receptionServiceName} />,
                           }}
                        />
                     </div>
                  </StyledFlex>
               </StyledFlex>
               <HealthcareProviderPatientPortalBoundServiceRemoveButton
                  boundServiceId={props.boundServiceId}
                  onSuccess={handleRemoveSuccess}
               />
               <ModalCancelButton onClick={closeBoundServiceRemoveModal} />
               <ModalCloseButton onClick={closeBoundServiceRemoveModal} />
            </Modal>
         ) : null}
      </>
   );
};
